<template>
  <div class="hms-page">
    <Navigation />

    <div class="container">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <img :src="$brand.logo" alt="HMS" class="hms-page-logo" />
          <div>
            <h4 class="hms-page-title">Dashboard</h4>
            <p class="hms-page-sub">{{ greeting }}, {{ firstName }} · {{ todayLong }}</p>
          </div>
        </div>
        <div class="d-flex gap-2">
          <select class="hms-input hms-period" v-model="period" @change="loadDashboard">
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
          </select>
          <button class="hms-icon-btn" @click="loadDashboard" :disabled="loading" title="Refresh">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="hms-spinner">
        <div class="spinner-border"></div>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Stat row (clickable) -->
        <div class="hms-stat-row">
          <div class="hms-stat" @click="goModule('/patients')" v-if="!isRestrictedRole">
            <div class="hms-stat-num">{{ num(p('Patients','total')) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.patients','Patients') }}</div>
            <div class="hms-stat-extra">+{{ num(p('Patients','today')) }} today</div>
          </div>
          <div class="hms-stat" @click="goModule('/admissions')" v-if="canSeeStat('/admissions')">
            <div class="hms-stat-num hms-stat-green">{{ num(p('Admissions','active')) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.active_admissions','Active Admissions') }}</div>
          </div>
          <div class="hms-stat" @click="goModule('/wards')" v-if="canSeeStat('/wards')">
            <div class="hms-stat-num hms-stat-amber">{{ num(p('Beds','available')) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.beds_available','Beds Available') }}</div>
            <div class="hms-stat-extra">{{ num(p('Beds','occupied')) }} occupied</div>
          </div>
          <div class="hms-stat" @click="goModule('/billing')" v-if="canSeeStat('/billing')">
            <div class="hms-stat-num hms-stat-orange">KSh {{ num(p('Revenue','today_revenue')) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.revenue_today','Collected Today') }}</div>
            <div class="hms-stat-extra">KSh {{ num(p('Revenue','billed')) }} billed</div>
          </div>
          <div class="hms-stat" @click="goModule('/lab')" v-if="canSeeStat('/lab')">
            <div class="hms-stat-num hms-stat-info">{{ num(p('LabPending','pending')) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.lab_pending','Lab Pending') }}</div>
          </div>
          <div class="hms-stat" @click="goModule('/queue')" v-if="canSeeStat('/queue')">
            <div class="hms-stat-num hms-stat-red">
              {{ p('WaitTime','avg_wait_min') !== '' && p('WaitTime','avg_wait_min') != null ? Math.round(p('WaitTime','avg_wait_min')) + 'm' : '—' }}
            </div>
            <div class="hms-stat-lbl">{{ t('stat.avg_wait','Avg Wait Time') }}</div>
            <div class="hms-stat-extra">{{ num(p('WaitTime','currently_waiting')) }} waiting now</div>
          </div>
          <div class="hms-stat" @click="goModule('/appointments')" v-if="canSeeStat('/appointments')">
            <div class="hms-stat-num hms-stat-purple">{{ num(p('AppointmentsToday','total')) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.appointments','Appointments') }}</div>
          </div>
          <div class="hms-stat" @click="goModule('/pharmacy')" v-if="lowStockCount > 0 && canSeeStat('/pharmacy')">
            <div class="hms-stat-num hms-stat-red">{{ num(lowStockCount) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.low_stock','Low Stock Items') }}</div>
          </div>
          <div class="hms-stat" @click="goModule('/queue')" v-if="canSeeStat('/queue')">
            <div class="hms-stat-num hms-stat-cyan">{{ num(p('QueueWaiting','waiting')) }}</div>
            <div class="hms-stat-lbl">{{ t('stat.queue_waiting','Queue Waiting') }}</div>
          </div>
          <div class="hms-stat" @click="goModule('/hrh')" :title="onShiftTooltip" v-if="canSeeStat('/hrh')">
            <div class="hms-stat-num hms-stat-purple">{{ onShift.length }}</div>
            <div class="hms-stat-lbl">{{ t('stat.on_shift','On Shift Now') }}</div>
            <div class="hms-stat-extra" v-if="onShiftSummary">{{ onShiftSummary }}</div>
          </div>
        </div>

        <!-- Two-column area -->
        <div class="row g-3">
          <!-- Left col — Quick Actions + Welcome card -->
          <div class="col-lg-8">
            <div class="hms-card">
              <div class="hms-card-title">{{ t('dash.quick_actions','Quick Actions') }}</div>
              <div class="d-flex flex-wrap gap-2">
                <button
                  v-for="qa in quickActions"
                  :key="qa.menuKey"
                  class="hms-btn hms-btn-primary"
                  @click="goModule(qa.route)"
                >
                  <i class="mdi me-1" :class="qa.icon"></i>{{ t(qa.i18n, qa.label) }}
                </button>
              </div>
            </div>

            <div class="hms-card">
              <div class="hms-card-title">Welcome to HMS</div>
              <p class="hms-card-text">
                You're signed in as <strong>{{ displayName || userName }}</strong>
                <span v-if="userRole"> ({{ prettyRole }})</span>.
                Use the modules on the right to navigate, or pick a quick action above.
              </p>
              <p class="hms-card-text-sm">
                <i class="mdi mdi-information-outline me-1"></i>
                Most modules are still being built — clicking a "soon" module will let you know.
              </p>
            </div>

            <!-- Visits & Admissions trends -->
            <div class="hms-card" v-if="trends && !isRestrictedRole">
              <div class="hms-card-title"><i class="mdi mdi-chart-timeline-variant me-2"></i>Visits &amp; Admissions</div>
              <div class="row g-3">
                <div class="col-md-6" v-for="tm in trendMetrics" :key="tm.key">
                  <div class="hms-trend">
                    <div class="hms-trend-head"><i class="mdi" :class="tm.icon" :style="{ color: tm.color }"></i> {{ tm.label }}</div>
                    <div class="hms-trend-row">
                      <div class="hms-trend-cell" v-for="pr in trendPeriods" :key="pr.key">
                        <div class="hms-trend-period">{{ pr.label }}</div>
                        <div class="hms-trend-val">{{ trends[tm.key][pr.key].value }}</div>
                        <div class="hms-trend-pct" :class="trends[tm.key][pr.key].pct >= 0 ? 'up' : 'down'">
                          <i class="mdi" :class="trends[tm.key][pr.key].pct >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'"></i>{{ Math.abs(trends[tm.key][pr.key].pct) }}%
                        </div>
                      </div>
                    </div>
                    <div class="hms-spark">
                      <div v-for="(d, i) in trends[tm.key].series" :key="i" class="hms-spark-bar"
                           :style="{ height: sparkH(trends[tm.key].series, d.count) + '%', background: tm.color }"
                           :title="d.date + ': ' + d.count"></div>
                    </div>
                    <div class="hms-trend-foot">Last 14 days · vs previous period</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sharon F#2 B12: Revenue by Department (top-5 quick view) -->
            <div class="hms-card mt-3" v-if="revenueByDept.length && canSeeStat('/billing')">
              <div class="hms-card-title"><i class="mdi mdi-cash-multiple me-2"></i>Revenue by Department <span class="hms-muted-sm">({{ period_label || 'today' }})</span></div>
              <div v-for="(d, idx) in revenueByDept.slice(0, 6)" :key="d.department" class="d-flex align-items-center gap-2 mb-2">
                <div style="min-width:120px;font-size:13px;font-weight:600;">{{ d.department }}</div>
                <div style="flex:1;height:18px;background:#f1f5f9;border-radius:9px;overflow:hidden;">
                  <div :style="{ width: (d.revenue / maxDeptRev * 100) + '%', background: deptColors[idx % deptColors.length], height: '100%', transition: 'width .3s' }"></div>
                </div>
                <div class="hms-mono" style="min-width:90px;text-align:right;font-size:13px;">KSh {{ Number(d.revenue || 0).toLocaleString() }}</div>
                <div class="hms-muted-sm" style="min-width:30px;text-align:right;">{{ d.invoices }}</div>
              </div>
              <div class="hms-muted-sm mt-2"><a href="javascript:void(0)" @click="$router.push('/revenue-by-department')">See full breakdown →</a></div>
            </div>
          </div>

          <!-- Right col — Modules list (grouped accordion) -->
          <div class="col-lg-4">
            <div class="hms-card">
              <div class="hms-card-title"><i class="mdi mdi-apps me-2"></i>{{ t('dash.platform_modules','Platform Modules') }}</div>
              <div class="hms-mod-search">
                <i class="mdi mdi-magnify"></i>
                <input v-model="moduleSearch" placeholder="Search modules…" class="hms-mod-search-input" />
                <i v-if="moduleSearch" class="mdi mdi-close-circle hms-mod-search-clear" @click="moduleSearch=''"></i>
              </div>

              <div v-for="g in filteredGroups" :key="g.key" class="hms-mod-group">
                <div class="hms-mod-group-head" @click="toggleGroup(g.key)">
                  <i class="mdi" :class="isGroupOpen(g.key) ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
                  <span class="hms-mod-group-label">{{ g.label }}</span>
                  <span class="hms-mod-group-count">{{ g.modules.length }}</span>
                </div>
                <div v-if="isGroupOpen(g.key)" class="hms-mod-group-body">
                  <div
                    v-for="mod in g.modules"
                    :key="mod.route"
                    class="hms-side-module"
                    :class="{ disabled: !mod.available }"
                    :title="mod.description || mod.label"
                    @click="goModule(mod.route, mod.available)"
                  >
                    <i :class="mod.icon" :style="{ color: mod.color }"></i>
                    <div class="hms-module-text">
                      <div class="hms-module-label">{{ mod.label }}</div>
                      <div v-if="mod.description" class="hms-module-desc">{{ mod.description }}</div>
                    </div>
                    <span v-if="!mod.available" class="hms-soon">soon</span>
                    <i class="mdi mdi-chevron-right ms-auto hms-module-chev"></i>
                  </div>
                </div>
              </div>

              <div v-if="!filteredGroups.length" class="hms-mod-empty">No modules match "{{ moduleSearch }}"</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast">
          <i class="mdi mdi-information-outline me-2"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:          #f0f0f0;
  --hms-s:          #a0a0a0;
  --hms-m:          #808080;
  --hms-bg1:        #141414;
  --hms-bg2:        #0a0a0a;
  --hms-bg3:        #1e1e1e;
  --hms-bg4:        #0f0f0f;
  --hms-br:         #2a2a2a;
  --hms-input-br:   #333333;
  --hms-placeholder:#4a4a4a;
  --hms-primary:    #1a7fe8;
  --hms-primary-lt: rgba(26,127,232,0.12);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] {
  --hms-h:          #1a1a1a;
  --hms-s:          #555555;
  --hms-m:          #777777;
  --hms-bg1:        #ffffff;
  --hms-bg2:        #f4f6f9;
  --hms-bg3:        #eef1f5;
  --hms-bg4:        #ffffff;
  --hms-br:         #dee2e8;
  --hms-input-br:   #ced4da;
  --hms-placeholder:#aaaaaa;
  --hms-primary:    #0047AB;
  --hms-primary-lt: rgba(0,71,171,0.08);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] body,
html[data-theme="dark"] body {
  background: var(--hms-bg2) !important;
  color: var(--hms-h) !important;
}
</style>

<style scoped>
.hms-page {
  min-height: 100vh;
  background: var(--hms-bg2);
  padding-top: 70px;
  color: var(--hms-h);
}

.hms-page-header { padding-top: 20px; flex-wrap: wrap; gap: 12px; }
.hms-page-logo { width: 40px; height: 40px; object-fit: contain; border-radius: 8px; }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; margin: 0; }
.hms-page-sub { color: var(--hms-m); font-size: 13px; margin: 0; }

.hms-input {
  background: var(--hms-bg4);
  border: 1px solid var(--hms-input-br);
  border-radius: 8px;
  color: var(--hms-h);
  padding: 6px 12px;
  font-size: 13px;
  outline: none;
  transition: border-color .15s;
}
.hms-input:focus { border-color: var(--hms-primary); }
.hms-period { width: auto; }

.hms-icon-btn {
  background: var(--hms-bg3);
  border: 1px solid var(--hms-br);
  color: var(--hms-primary);
  width: 36px; height: 36px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
  cursor: pointer;
  transition: all .15s;
}
.hms-icon-btn:hover { background: var(--hms-primary-lt); border-color: var(--hms-primary); }
.hms-icon-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.hms-spinner { display: flex; justify-content: center; padding: 60px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }

/* Cards */
.hms-card {
  background: var(--hms-bg1);
  border: 1px solid var(--hms-br);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
}
.hms-card-title { color: var(--hms-h); font-size: 15px; font-weight: 700; margin-bottom: 14px; }
.hms-card-text { color: var(--hms-m); font-size: 14px; line-height: 1.6; margin: 0 0 8px; }

/* Visits & Admissions trends */
.hms-trend { border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px; background: var(--hms-bg2); height: 100%; }
.hms-trend-head { font-size: 14px; font-weight: 700; color: var(--hms-h); margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
.hms-trend-row { display: flex; gap: 8px; }
.hms-trend-cell { flex: 1; text-align: center; background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; padding: 8px 4px; }
.hms-trend-period { font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: var(--hms-s); }
.hms-trend-val { font-size: 20px; font-weight: 800; color: var(--hms-h); line-height: 1.2; }
.hms-trend-pct { font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; gap: 1px; }
.hms-trend-pct.up { color: #16a34a; }
.hms-trend-pct.down { color: #dc2626; }
.hms-spark { display: flex; align-items: flex-end; gap: 3px; height: 48px; margin-top: 12px; padding-top: 4px; }
.hms-spark-bar { flex: 1; min-height: 2px; border-radius: 2px 2px 0 0; opacity: .85; }
.hms-trend-foot { font-size: 10px; color: var(--hms-s); margin-top: 6px; text-align: right; }
.hms-card-text-sm { color: var(--hms-s); font-size: 12px; margin: 0; }

/* Stats */
.hms-stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.hms-stat {
  background: var(--hms-bg1);
  border: 1px solid var(--hms-br);
  border-radius: 12px;
  padding: 16px 20px;
  flex: 1;
  min-width: 160px;
  cursor: pointer;
  transition: all .2s;
}
.hms-stat:hover {
  border-color: var(--hms-primary);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px var(--hms-primary-lt);
}
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-extra { font-size: 11px; color: var(--hms-m); margin-top: 2px; }
.hms-stat-green  { color: #34c38f; }
.hms-stat-amber  { color: #f1b44c; }
.hms-stat-orange { color: var(--hms-accent); }
.hms-stat-info   { color: #50a5f1; }
.hms-stat-purple { color: #9b59b6; }
.hms-stat-red    { color: var(--hms-danger); }
.hms-stat-cyan   { color: #1abc9c; }

/* Buttons */
.hms-btn {
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
}
.hms-btn-primary {
  background: var(--hms-primary-lt);
  color: var(--hms-primary);
  border: 1px solid var(--hms-primary-lt);
}
.hms-btn-primary:hover { background: var(--hms-primary); color: #fff; border-color: var(--hms-primary); }

/* Side modules */
.hms-side-module {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--hms-br);
  cursor: pointer;
  color: var(--hms-h);
  font-size: 14px;
  transition: all .15s;
}
.hms-side-module:hover { color: var(--hms-primary); padding-left: 4px; }
.hms-side-module:last-child { border-bottom: none; }
.hms-side-module > i:first-child { font-size: 20px; width: 28px; text-align: center; flex-shrink: 0; margin-top: 1px; }
.hms-module-text { flex: 1; min-width: 0; }
.hms-module-label { font-size: 13px; line-height: 1.3; }
.hms-module-desc  { font-size: 11px; color: var(--hms-s); margin-top: 2px; line-height: 1.4; }
.hms-side-module.disabled { opacity: 0.55; cursor: pointer; }
.hms-side-module.disabled:hover { color: var(--hms-h); padding-left: 0; }
.hms-soon {
  background: var(--hms-bg3);
  color: var(--hms-s);
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 4px;
}
.hms-module-chev { color: var(--hms-s); font-size: 16px; }

/* Module search */
.hms-mod-search {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--hms-bg2);
  border: 1px solid var(--hms-br);
  border-radius: 6px;
  padding: 4px 8px;
  margin-bottom: 8px;
}
.hms-mod-search > .mdi:first-child { color: var(--hms-s); font-size: 16px; margin-right: 6px; }
.hms-mod-search-input {
  flex: 1; border: none; background: transparent; outline: none;
  font-size: 13px; color: var(--hms-h); padding: 6px 0;
}
.hms-mod-search-clear { color: var(--hms-s); cursor: pointer; font-size: 16px; }
.hms-mod-search-clear:hover { color: var(--hms-h); }

/* Module accordion */
.hms-mod-group { border-bottom: 1px solid var(--hms-br); }
.hms-mod-group:last-child { border-bottom: none; }
.hms-mod-group-head {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 0; cursor: pointer; user-select: none;
  color: var(--hms-h); font-weight: 600; font-size: 12px;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.hms-mod-group-head:hover { color: var(--hms-primary); }
.hms-mod-group-head > .mdi { font-size: 18px; color: var(--hms-s); }
.hms-mod-group-label { flex: 1; }
.hms-mod-group-count {
  background: var(--hms-bg3); color: var(--hms-s);
  font-size: 11px; padding: 1px 8px; border-radius: 10px; font-weight: 600;
}
.hms-mod-group-body { padding: 0 0 6px 6px; }
.hms-mod-group-body .hms-side-module { padding: 9px 0; font-size: 13px; }
.hms-mod-group-body .hms-side-module i:first-child { font-size: 18px; }

.hms-mod-empty { padding: 20px 8px; text-align: center; color: var(--hms-s); font-size: 13px; }

/* Toast */
.hms-toast {
  position: fixed;
  bottom: 24px; right: 24px;
  background: var(--hms-bg1);
  border: 1px solid var(--hms-primary);
  color: var(--hms-h);
  padding: 12px 18px;
  border-radius: 10px;
  box-shadow: 0 12px 32px var(--hms-primary-lt);
  z-index: 1000;
  font-size: 13px;
  display: flex;
  align-items: center;
}
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

/* Responsive */
@media (max-width: 768px) {
  .hms-stat-row .hms-stat { min-width: 100%; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";
import { canAccessModule, isAdminRole, ROLE_ROUTES } from "@/roles";

export default {
  name: "Dashboard",
  components: { Navigation },
  data() {
    return {
      loading: true,
      period:  "today",
      stats:   {},
      onShift: [],
      i18n:    useI18n(),
      toastMsg: "",
      toastTimer: null,

      // Session
      userName:    sessionStorage.getItem("userName")    || "",
      displayName: sessionStorage.getItem("displayName") || "",
      userRole:    sessionStorage.getItem("userRole")    || "",
      hospitalId:  sessionStorage.getItem("hospitalId"),
      branchId:    sessionStorage.getItem("branchId"),

      trends: null,
      period_label: "today",
      revenueByDept: [],
      maxDeptRev: 1,
      deptColors: ["#3b82f6","#ef4444","#10b981","#f59e0b","#8b5cf6","#ec4899","#06b6d4","#84cc16"],
      trendMetrics: [
        { key: "visits",     label: "Visits",     icon: "mdi-account-arrow-right-outline", color: "#0EA5E9" },
        { key: "admissions", label: "Admissions", icon: "mdi-bed",                         color: "#16a085" }
      ],
      trendPeriods: [
        { key: "today", label: "Today" },
        { key: "week",  label: "This week" },
        { key: "month", label: "This month" }
      ],

      quickActions: [
        { icon: "mdi-account-plus",       label: "Register Patient",  i18n: "qa.register_patient", route: "/patients?action=add",  menuKey: "patients" },
        { icon: "mdi-stethoscope",        label: "Triage",            i18n: "qa.triage",           route: "/triage",       menuKey: "triage" },
        { icon: "mdi-ticket",             label: "Issue Ticket",      i18n: "qa.issue_ticket",     route: "/queue",        menuKey: "queue" },
        { icon: "mdi-cash-plus",          label: "Billing",           i18n: "qa.billing",          route: "/billing",      menuKey: "billing" },
        { icon: "mdi-flask-outline",      label: "Lab Request",       i18n: "qa.new_lab",          route: "/lab",          menuKey: "lab" },
        { icon: "mdi-radiology-box",      label: "Radiology",         i18n: "qa.radiology",        route: "/radiology",    menuKey: "radiology" },
        { icon: "mdi-pill",               label: "Dispense Drug",     i18n: "qa.dispense_drug",    route: "/pharmacy",     menuKey: "pharmacy" },
        { icon: "mdi-bed-empty",          label: "New Admission",     i18n: "qa.new_admission",    route: "/wards",        menuKey: "wards" },
      ],

      moduleSearch: "",
      featureFlags: {},     // feature_code → 0 (disabled) | 1 (enabled). Absence = enabled.
      openGroups: { frontoffice: true, finance: false, clinical: false, maternal: false, diagnostics: false, programs: false, community: false, ops: false, system: false },
      moduleGroups: [
        { key: "clinical", label: "Clinical Care", modules: [
          { icon: "mdi mdi-hospital",             label: "ED Board",                description: "Emergency Department — live patient tracker by acuity", route: "/ed-board",       color: "#dc3545", available: true },
          { icon: "mdi mdi-stethoscope",          label: "Triage Station",          description: "Rapid sorting of incoming patients by urgency", route: "/triage",         color: "#fd7e14", available: true },
          { icon: "mdi mdi-doctor",               label: "Consultation",            description: "Doctor's consult room — diagnosis, prescriptions, lab orders", route: "/consultation",   color: "#1abc9c", available: true },
          { icon: "mdi mdi-bed-empty",            label: "Admissions",              description: "Inpatient ward admissions and discharges", route: "/admissions",     color: "#16a085", available: true },
          { icon: "mdi mdi-bed",                  label: "Wards & Beds",            description: "Bed status across all wards (free / occupied / cleaning)", route: "/wards",          color: "#34c38f", available: true },
          { icon: "mdi mdi-broom",                label: "Bed Cleaning",            description: "Housekeeping queue — beds awaiting cleaning between patients", route: "/housekeeping",   color: "#10B981", available: true },
          { icon: "mdi mdi-heart-pulse",          label: "Vitals & NEWS2",          description: "Vital signs (BP, HR, temp) with WHO Early Warning Score", route: "/vitals",         color: "#EF4444", available: true },
          { icon: "mdi mdi-medical-bag",          label: "Theatre",                 description: "Operating-room bookings, surgery list, theatre log", route: "/theatre",        color: "#e74c3c", available: true, feature_code: "theatre" },
          { icon: "mdi mdi-iv-bag",                label: "Anaesthesia Record",      description: "Pre-op assessment, anaesthesia chart, recovery notes", route: "/anaesthesia",    color: "#7C3AED", available: true, feature_code: "theatre" },
          { icon: "mdi mdi-clipboard-list-outline", label: "Medication Admin (MAR)", description: "Nurse's medication administration record — what was given and when", route: "/mar",            color: "#06B6D4", available: true }
        ]},
        { key: "maternal", label: "Maternal & Child Health", modules: [
          { icon: "mdi mdi-baby-carriage",        label: "Maternity",               description: "Day-to-day labour, delivery, ANC and partograph (the clinician's working screen)", route: "/maternity",          color: "#EC4899", available: true, feature_code: "maternal_safety" },
          { icon: "mdi mdi-shield-cross",         label: "Maternal Safety",         description: "Risk scoring, pre-eclampsia, PPH bundle, PMTCT, MPDSR — the death-prevention command centre", route: "/maternal-safety",    color: "#BE123C", available: true, feature_code: "maternal_safety" },
          { icon: "mdi mdi-chart-timeline-variant", label: "Maternal Mortality",    description: "Population view: MMR, ICD-MM causes, 3-Delays, near-miss register, trend over time", route: "/maternal-mortality", color: "#991B1B", available: true, feature_code: "maternal_safety" },
          { icon: "mdi mdi-baby-face",            label: "NICU / Neonatal",         description: "Newborn Intensive Care Unit — care for sick, preterm or low-birth-weight babies", route: "/nicu",               color: "#F472B6", available: true, feature_code: "nicu" },
          { icon: "mdi mdi-human-pregnant",       label: "Family Planning",         description: "Contraceptive methods, new acceptors, refills, discontinuations", route: "/fp",                 color: "#DB2777", available: true },
          { icon: "mdi mdi-needle",               label: "Immunization (KEPI)",     description: "Kenya Expanded Programme on Immunization — childhood vaccine schedule (BCG, OPV, Penta, MR, etc.)", route: "/immunization",       color: "#10B981", available: true },
          { icon: "mdi mdi-food-apple",           label: "SAM / RUTF Register",     description: "Severe Acute Malnutrition register with Plumpy'Nut therapeutic feeding follow-up", route: "/sam-register",       color: "#B45309", available: true },
          { icon: "mdi mdi-account-heart",        label: "Women's Health Lifecycle", description: "Pre-conception, IPV screening, menopause clinic, cancer survivorship in one place", route: "/womens-health",     color: "#DB2777", available: true },
          { icon: "mdi mdi-timeline-text-outline", label: "Patient Life Timeline",  description: "One patient → every clinical event from in-utero to today, in chronological order", route: "/timeline",           color: "#1E40AF", available: true }
        ]},
        { key: "diagnostics", label: "Diagnostics & Pharmacy", modules: [
          { icon: "mdi mdi-flask",                label: "Laboratory",              description: "Order, track and review lab tests (blood, urine, microbiology, etc.)", route: "/lab",            color: "#50a5f1", available: true, feature_code: "lab" },
          { icon: "mdi mdi-flask-empty-outline",  label: "Lab Results Entry",       description: "Lab technologist's screen to enter test results", route: "/lab/results",    color: "#1abc9c", available: true, feature_code: "lab" },
          { icon: "mdi mdi-radioactive",          label: "Radiology",               description: "X-ray, ultrasound, CT, MRI requests and reports", route: "/radiology",      color: "#9b59b6", available: true, feature_code: "radiology" },
          { icon: "mdi mdi-pill",                 label: "Pharmacy",                description: "Drug dispensing, prescriptions, stock control", route: "/pharmacy",       color: "#f1b44c", available: true, feature_code: "pharmacy" },
          { icon: "mdi mdi-water",                label: "Blood Bank",              description: "Blood donations, cross-matching, transfusion requests", route: "/blood-bank",     color: "#B91C1C", available: true },
          { icon: "mdi mdi-shield-sun",           label: "CSSD",                    description: "Central Sterile Services — instrument sterilisation log", route: "/cssd",           color: "#0F766E", available: true }
        ]},
        { key: "programs", label: "Programs & Registers", modules: [
          { icon: "mdi mdi-ribbon",               label: "HIV / ART Register",      description: "HIV patients on Antiretroviral Therapy (lifelong treatment)", route: "/art",            color: "#DC2626", available: true },
          { icon: "mdi mdi-lungs",                label: "TB Register (TIBU)",      description: "Tuberculosis case register linked to Kenya's TIBU national system", route: "/tb",             color: "#7C3AED", available: true },
          { icon: "mdi mdi-clipboard-pulse",      label: "Disease Registries",      description: "Diabetes, hypertension, asthma, cancer — chronic disease cohorts", route: "/registry",       color: "#e84393", available: true },
          { icon: "mdi mdi-clipboard-pulse-outline", label: "Specialty Registries", description: "Cardiac, renal, neurological and other specialty clinics", route: "/specialty",      color: "#0891B2", available: true },
          { icon: "mdi mdi-brain",                label: "Mental Health",           description: "Depression, anxiety, psychosis — screening, treatment, follow-up", route: "/mental-health",  color: "#9333EA", available: true, feature_code: "mental_health" },
          { icon: "mdi mdi-shield-account",       label: "SGBV / GBV-RC",           description: "Sexual / gender-based violence — confidential one-stop response centre", route: "/sgbv",           color: "#BE123C", available: true },
          { icon: "mdi mdi-grave-stone",          label: "Mortality / M&M",         description: "Hospital-wide death audit and morbidity & mortality review", route: "/mortality",      color: "#475569", available: true },
          { icon: "mdi mdi-grave-stone",          label: "Funeral Home",            description: "Body storage register — admission, release, viewing, repatriation", route: "/funeral-home",   color: "#7f8c8d", available: true, feature_code: "mortuary" }
        ]},
        { key: "community", label: "Community & Outreach", modules: [
          { icon: "mdi mdi-account-heart",        label: "CHV Companion",           description: "Community Health Volunteer mobile screen — defaulter trace, home visits, SBAR referrals", route: "/chv",            color: "#059669", available: true },
          { icon: "mdi mdi-account-heart-outline", label: "Community Health (CHU/CHP)", description: "Community Health Unit / Practitioner: village-level service register", route: "/community",  color: "#10B981", available: true },
          { icon: "mdi mdi-map-marker-radius",    label: "Facility Network",        description: "Map of nearby health facilities + KEPH-level capabilities for referrals", route: "/geo",            color: "#0EA5E9", available: true },
          { icon: "mdi mdi-truck-fast-outline",   label: "Referrals",               description: "Outgoing / incoming referrals between facilities with SBAR handover", route: "/referrals",      color: "#9b59b6", available: true },
          { icon: "mdi mdi-ambulance",            label: "Ambulance Service",       description: "Fleet, crew, live GPS tracking + dispatch console for emergency transport", route: "/ambulance",      color: "#dc3545", available: true, feature_code: "ambulance" },
          { icon: "mdi mdi-video-outline",        label: "Telehealth",              description: "Remote video consultations for distant patients or specialist input", route: "/telehealth",     color: "#7C3AED", available: true, feature_code: "telemed" }
        ]},
        { key: "frontoffice", label: "Patients & Front Office", modules: [
          { icon: "mdi mdi-account-multiple-outline", label: "Patients & EMR",      description: "Patient registration and Electronic Medical Record (the master patient file)", route: "/patients",       color: "#1a7fe8", available: true },
          { icon: "mdi mdi-ticket-confirmation",  label: "Queue & Tickets",         description: "Real-time queue display per service point (consultation, pharmacy, lab)", route: "/queue",          color: "#1abc9c", available: true },
          { icon: "mdi mdi-calendar-clock",       label: "Appointments",            description: "Future-dated clinic bookings with SMS reminders", route: "/appointments",   color: "#9b59b6", available: true },
          { icon: "mdi mdi-account-circle-outline", label: "Patient Portal",        description: "Patient-facing app: view results, book appointments, see vaccination card", route: "/portal/login",   color: "#1A4480", available: true },
          { icon: "mdi mdi-comment-quote",        label: "Patient Feedback",        description: "Patient satisfaction surveys, complaints, compliments", route: "/feedback",       color: "#0EA5E9", available: true }
        ]},
        { key: "finance", label: "Finance & SHA", modules: [
          { icon: "mdi mdi-cash-multiple",        label: "Billing",                 description: "Charge sheet, invoices, payment collection at every service point", route: "/billing",          color: "#F5821F", available: true },
          { icon: "mdi mdi-shield-check-outline", label: "Insurance Claims",        description: "Generate, submit and track insurance claims (private + public)", route: "/insurance",        color: "#0EA5E9", available: true, feature_code: "sha_claims" },
          { icon: "mdi mdi-handshake-outline",    label: "Insurance Rate Cards",    description: "Per-provider negotiated pricing (AAR, Jubilee, SHA, Britam) — auto-applied at billing", route: "/insurance-rates", color: "#7C3AED", available: true },
          { icon: "mdi mdi-shield-key-outline",   label: "SHA Integration",         description: "Kenya's Social Health Authority — eligibility check + e-claims (replaced NHIF)", route: "/sha",              color: "#0284C7", available: true, feature_code: "sha_claims" },
          { icon: "mdi mdi-shield-check",         label: "Ntutu Care Cover",        description: "County resident health cover — eligibility and claim routing", route: "/ntutu",            color: "#047857", available: true, feature_code: "ntutu" },
          { icon: "mdi mdi-heart-pulse",          label: "Wellness / Health Talks", description: "Patient-facing wellness articles, immunisation and nutrition content — admin-authored, surfaced on the portal", route: "/wellness",         color: "#DB2777", available: true, feature_code: "wellness" },
          { icon: "mdi mdi-swap-horizontal",      label: "Shift Handover",          description: "Outgoing-to-incoming staff handover: critical patients, pending tasks, equipment, meds — with supervisor approval and TAT", route: "/shift-handover",   color: "#7C3AED", available: true, feature_code: "shift_handover" },
          { icon: "mdi mdi-cash-multiple",        label: "Revenue by Act",          description: "Revenue split by Act of Parliament: FIF, PHC Fund, SHA, donor funds, etc.", route: "/revenue-acts",     color: "#16A34A", available: true },
          { icon: "mdi mdi-finance",              label: "Revenue by Department",   description: "Revenue grouped by department & activity (consultation, lab, pharmacy, theatre, ward…)", route: "/revenue-by-department", color: "#0EA5E9", available: true },
          { icon: "mdi mdi-cash-register",        label: "Finance Controls",        description: "Cash collection, daily reconciliation, deposit slips, audit trail", route: "/finance-controls", color: "#15803D", available: true },
          { icon: "mdi mdi-file-chart-outline",   label: "MoH Reports (705/710/717/718)", description: "Kenya MoH monthly summary forms — auto-generated from your data, ready to submit to county", route: "/moh-reports", color: "#1E3A8A", available: true }
        ]},
        { key: "ops", label: "Operations & HR", modules: [
          { icon: "mdi mdi-warehouse",            label: "Stores & Procurement",    description: "Medical supplies, drugs, consumables — stock levels and purchase orders", route: "/stores",            color: "#0891B2", available: true },
          { icon: "mdi mdi-tools",                label: "Equipment Register",      description: "Asset register: machines, beds, devices — service history and maintenance", route: "/equipment",         color: "#475569", available: true },
          { icon: "mdi mdi-account-group-outline", label: "HRH (Organogram + Roster)", description: "Human Resources for Health — staff cadres, shift rosters, on-call lists", route: "/hrh",            color: "#3498DB", available: true },
          { icon: "mdi mdi-account-clock-outline", label: "Staff Availability",      description: "Who is on duty, on leave, or off — by cadre (nurses, surgeons, anaesthetists…)", route: "/staff-availability", color: "#2563EB", available: true },
          { icon: "mdi mdi-account-tie-outline",  label: "HR Self-Service",         description: "Staff portal: payslips, leave requests, PIP, training records", route: "/hr-self-service",   color: "#0E7490", available: true },
          { icon: "mdi mdi-account-tie",          label: "Staff & HR",              description: "HR admin: staff records, contracts, performance reviews", route: "/admin/staff",       color: "#3498db", available: true },
          { icon: "mdi mdi-format-list-bulleted-square", label: "Service Catalog",  description: "Master price list of every service the facility offers (linked to billing)", route: "/admin/services",    color: "#0891B2", available: true }
        ]},
        { key: "system", label: "Analytics & System", modules: [
          { icon: "mdi mdi-view-dashboard-variant", label: "Executive Dashboards",  description: "Top-line view for hospital leadership: revenue, throughput, quality, mortality", route: "/exec",            color: "#1E3A8A", available: true },
          { icon: "mdi mdi-finance",               label: "Hospital KPI Dashboard", description: "Daily-monitored Key Performance Indicators with traffic-light status", route: "/kpi",             color: "#0F766E", available: true },
          { icon: "mdi mdi-chart-box-outline",    label: "Reports & KPIs",          description: "Custom report builder + KPI library", route: "/reports",         color: "#0EA5E9", available: true },
          { icon: "mdi mdi-chart-timeline-variant", label: "Visits Summary",        description: "Executive view: visits + revenue with day/week/month comparisons + department breakdown", route: "/visits-summary", color: "#8B5CF6", available: true },
          { icon: "mdi mdi-office-building-cog-outline", label: "Department Overview", description: "Per-department workload for department heads: queue performance, visits, staffing, billed revenue", route: "/department-overview", color: "#0F766E", available: true },
          { icon: "mdi mdi-robot-outline",        label: "AI Assistant",            description: "Claude-powered helper for clinical decision support, drafting notes, and answering questions", route: "/ai",              color: "#1abc9c", available: true, feature_code: "ai_assist" },
          { icon: "mdi mdi-transit-connection-variant", label: "Digital Health (DHIS2/KMHFL)", description: "Integration with national systems: DHIS2 (aggregate data) + KMHFL (facility list)", route: "/digital-health", color: "#1E40AF", available: true, feature_code: "icd11_interop" },
          { icon: "mdi mdi-message-text-outline", label: "SMS Outbox",              description: "Outgoing SMS log: reminders, alerts, defaulter trace messages", route: "/sms",             color: "#10B981", available: true },
          { icon: "mdi mdi-bell-outline",         label: "Notifications",           description: "System-wide notification centre: alerts, reminders, system events", route: "/notifications",   color: "#F59E0B", available: true },
          { icon: "mdi mdi-clock-outline",        label: "Cron / Scheduled Jobs",   description: "Background jobs: reminder SMS sender, defaulter trace, MPDSR deadline tracker", route: "/cron",            color: "#475569", available: true },
          { icon: "mdi mdi-shield-account",       label: "Users & Roles",           description: "System users with role-based access control (who can see / do what)", route: "/admin/users",     color: "#6366F1", available: true },
          { icon: "mdi mdi-source-branch",        label: "Branches",                description: "For chains/networks: multi-facility configuration (Level 4 + satellites)", route: "/admin/branches",  color: "#8B5CF6", available: true },
          { icon: "mdi mdi-shield-search",        label: "Audit Log",               description: "Tamper-proof record of every action — who changed what, when, from where", route: "/admin/audit",     color: "#64748B", available: true },
          { icon: "mdi mdi-shield-lock-outline",  label: "Data Governance",         description: "Data classification register + role-based access matrix (DHA/DPIA compliance)", route: "/admin/data-governance", color: "#0EA5E9", available: true },
          { icon: "mdi mdi-tune-variant",         label: "Workflow & Billing Settings", description: "Configure billing departments and save-&-queue routing for your facility", route: "/admin/workflow-settings", color: "#7C3AED", available: true },
          { icon: "mdi mdi-cog-outline",          label: "Settings",                description: "Facility info, branding, modules toggle, system preferences", route: "/settings",        color: "#94A3B8", available: true }
        ]}
      ]
    };
  },
  computed: {
    // A "restricted" role is a known non-admin role with an explicit route whitelist.
    // Hospital-wide stats (total patients, trends) are hidden from them.
    isRestrictedRole() {
      return !!this.userRole && !isAdminRole(this.userRole) && !!ROLE_ROUTES[this.userRole];
    },
    firstName() {
      var n = this.displayName || this.userName || "";
      if (!n) return "there";
      return n.trim().split(/\s+/)[0];
    },
    greeting() {
      var h = new Date().getHours();
      if (h < 12) return "Good morning";
      if (h < 18) return "Good afternoon";
      return "Good evening";
    },
    todayLong() {
      return new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    },
    prettyRole() {
      if (!this.userRole) return "";
      return this.userRole.split('_').map(function(s){
        return s.charAt(0).toUpperCase() + s.slice(1);
      }).join(' ');
    },
    lowStockCount() {
      return this.p('LowStock','count');
    },
    onShiftSummary() {
      if (!this.onShift.length) return '';
      var byShift = {};
      this.onShift.forEach(function(r){ byShift[r.shift_type] = (byShift[r.shift_type] || 0) + 1; });
      return Object.keys(byShift).map(function(k){ return byShift[k] + ' ' + k; }).join(' · ');
    },
    onShiftTooltip() {
      if (!this.onShift.length) return 'No staff currently on shift';
      return this.onShift.slice(0, 12).map(function(r){ return (r.cadre_name || '—') + ': ' + r.first_name + ' ' + r.last_name + ' (' + r.shift_type + ')'; }).join('\n');
    },
    modules() {
      return this.moduleGroups.reduce(function(acc, g){ return acc.concat(g.modules); }, []);
    },
    filteredGroups() {
      var q = (this.moduleSearch || "").trim().toLowerCase();
      var featureMap = this.featureFlags || {};
      var featureOn = function(code) {
        // Absence = enabled (default ON). Only the explicit `0` hides.
        return !code || featureMap[code] !== 0;
      };
      // Role-based visibility — restricted roles (e.g. triage_001) only see their scope.
      // Admin roles + unknown roles fail-open (see src/roles.js).
      var roleCode = this.userRole || null;
      var canSee = function(route) {
        if (!roleCode) return true;
        return canAccessModule(roleCode, route);
      };
      // Lead groups: Patients & Front Office and Finance (Billing) come first,
      // then the rest in their defined order (Narok feedback C-1 intent).
      var leadOrder = ["frontoffice", "finance"];
      return this.moduleGroups
        .map(function(g){ return { key: g.key, label: g.label,
          modules: g.modules.filter(function(m){
            // Hide modules whose feature_code is explicitly disabled for this org.
            if (!featureOn(m.feature_code)) return false;
            // Hide modules this role has no access to.
            if (!canSee(m.route)) return false;
            if (!q) return true;
            var hay = ((m.label || '') + ' ' + (m.description || '') + ' ' + (m.route || '')).toLowerCase();
            return hay.indexOf(q) !== -1;
          }) }; })
        .filter(function(g){ return g.modules.length > 0; })
        .sort(function(a, b){
          var ai = leadOrder.indexOf(a.key), bi = leadOrder.indexOf(b.key);
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadDashboard();
    this.loadFeatureFlags();
    this.loadTrends();
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    isGroupOpen(key) {
      // When searching, force all groups open so matched modules are visible
      if ((this.moduleSearch || "").trim()) return true;
      return !!this.openGroups[key];
    },
    toggleGroup(key) {
      this.openGroups = Object.assign({}, this.openGroups, { [key]: !this.openGroups[key] });
    },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    num(v) {
      if (v === null || v === undefined || v === "") return 0;
      return Number(v).toLocaleString("en-KE");
    },
    canSeeStat(route) {
      if (!this.userRole) return true;
      return canAccessModule(this.userRole, route);
    },
    p(group, key) {
      var s = this.stats || {};
      var g = s[group];
      if (!g) return 0;
      return g[key] || 0;
    },
    showToast(msg) {
      this.toastMsg = msg;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(function(){ self.toastMsg = ""; }, 2400);
    },
    async loadTrends() {
      try {
        var r = await executeJson({ function: "getVisitAdmissionTrends", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.trends = r.data.data;
      } catch (e) { /* non-fatal */ }
    },
    async loadRevenueByDept() {
      // Convert dashboard period to date_from/date_to range
      var d = new Date(); var pad = function(n){ return n < 10 ? "0"+n : ""+n; };
      var iso = function(dt){ return dt.getFullYear()+"-"+pad(dt.getMonth()+1)+"-"+pad(dt.getDate()); };
      var to = iso(d); var from = to;
      if (this.period === "this_week") {
        var dow = (d.getDay() + 6) % 7;  // make Monday=0
        var mon = new Date(d); mon.setDate(d.getDate() - dow); from = iso(mon);
      } else if (this.period === "this_month") {
        from = d.getFullYear()+"-"+pad(d.getMonth()+1)+"-01";
      }
      try {
        var r = await executeJson({ function: "getRevenueByDepartment", date_from: from, date_to: to }, hms);
        if (r.data && r.data.success) {
          var rows = r.data.data.RevenueByDepartment || r.data.data.rows || r.data.data.departments || [];
          // Normalise field names so the template works regardless of exact response shape
          this.revenueByDept = rows.map(function(d){
            return {
              department: d.department || d.department_name || d.label || "—",
              revenue:   Number(d.revenue || d.total || d.amount || 0),
              invoices:  Number(d.invoices || d.invoice_count || d.count || 0)
            };
          }).sort(function(a,b){ return b.revenue - a.revenue; });
          this.maxDeptRev = Math.max.apply(null, this.revenueByDept.map(function(d){ return d.revenue; }).concat([1]));
        }
      } catch (e) { /* non-fatal */ }
    },
    sparkH(series, count) {
      var max = Math.max.apply(null, series.map(function(s){ return s.count; }).concat([1]));
      return max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 4;
    },
    goModule(route, available) {
      if (available === false) {
        this.showToast("Coming soon");
        return;
      }
      var mod = this.modules.find(function(m){ return m.route === route; });
      if (mod && !mod.available) {
        this.showToast(mod.label + " — coming soon");
        return;
      }
      this.$router.push(route).catch(function(){});
    },
    async loadFeatureFlags() {
      try {
        var r = await executeJson({ function: "getMyFeatureFlags" }, hms);
        if (r.data && r.data.success && r.data.data && Array.isArray(r.data.data.features)) {
          var map = {};
          r.data.data.features.forEach(function(f){ map[f.feature_code] = parseInt(f.is_enabled, 10); });
          this.featureFlags = map;
        }
      } catch (e) {
        // Silent — defaults to all-enabled if the call fails.
      }
    },
    async loadDashboard() {
      this.loading = true;
      try {
        var r = await executeJson(
          { function: "getDashboardStats", branch_id: this.branchId, hospital_id: this.hospitalId, period: this.period },
          hms
        );
        if (r.data && r.data.success) {
          this.stats = r.data.data || {};
          this.period_label = r.data.data.period_label || this.period;
        }
        this.loadRevenueByDept();
      } catch (e) {
        console.error("Dashboard error:", e);
      }
      try {
        var s = await executeJson({ function: "getOnShiftNow" }, hms);
        if (s.data && s.data.success) this.onShift = (s.data.data && s.data.data.on_shift) || [];
      } catch(e) { /* offline ok */ }
      this.loading = false;
    }
  }
};
</script>
